// "Near you" on the scenario card (Trent, 2026-09-29): what THIS scenario costs
// at the hospitals around the reader, on the same national spread the card
// already draws.
//
//   · the national band (p10 / median / average / p90) with the reader's local
//     range shaded on it and the nearest hospital pinned;
//   · the nearest hospital, the cheapest and the priciest within the radius,
//     each with where its blended price ranks among U.S. hospitals;
//   · the nearest five, with the drive verdict against the nearest;
//   · one location + radius control (radius auto-widens until 5 hospitals
//     price the scenario, unless the reader picked one).
//
// Nothing here prices anything of its own: blended bills come from the same
// per-scenario coverage shard the page's hospital list reads (the hospital's
// own prices, any code it doesn't publish filled at the U.S. average), with
// the shared plausibility gate and drive verdict from lib/localSavings.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, LocateFixed, MapPin } from "lucide-react";
import { PriceRangeBand } from "@/components/PriceRangeBand";
import NearbyQualityTable from "@/components/scenarios/NearbyQualityTable";
import { titleCaseCity, titleCaseHospital } from "@/lib/hospitalName";
import { NEAR_RADII, MIN_NEARBY, rankText, useLocalScenarioPrices, type LocalHospital as Local } from "@/hooks/useLocalScenarioPrices";
import type { DriveVerdict } from "@/lib/localSavings";
import type { UserLocation } from "@/hooks/useUserLocation";

interface Props {
  scenarioId: string;
  /** U.S. average complete bill — the blend's fill value and the gate's baseline. */
  nationalTotal: number;
  band: { p10: number | null; median: number | null; avg: number | null; p90: number | null };
  dark: boolean;
  location: UserLocation | null;
  isLocating: boolean;
  error: string | null;
  onDetect: () => void;
  onZip: (zip: string) => void;
  radius: number;
  onRadius: (mi: number) => void;
}

const fmt = (v: number) => "$" + Math.round(v).toLocaleString("en-US");

export default function ScenarioLocalPrices(p: Props) {
  const { scenarioId, nationalTotal, band, dark, location, radius } = p;
  const [radiusTouched, setRadiusTouched] = useState(false);
  const [zipOpen, setZipOpen] = useState(false);
  const [zip, setZip] = useState("");
  const { loaded, ranked, all, local, nearest, cheapest, priciest, verdictOf, localRange } = useLocalScenarioPrices(scenarioId, nationalTotal, location, radius);
  const pool = loaded;

  // Widen (never narrow) until MIN_NEARBY hospitals price this — only while the
  // reader hasn't picked a radius. The page opens at 120 mi (Trent's radius);
  // this only matters when something else set a smaller one.
  useEffect(() => {
    if (radiusTouched || !location || !pool) return;
    if (all.filter((l) => l.miles <= radius).length >= MIN_NEARBY) return;
    const wider = NEAR_RADII.find((r) => r > radius && all.filter((l) => l.miles <= r).length >= MIN_NEARBY) ?? NEAR_RADII[NEAR_RADII.length - 1];
    if (wider !== radius) p.onRadius(wider);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all, radiusTouched, location, pool, radius]);

  // ---- palette (light / dark), AA on both grounds -----------------------------
  const C = dark ? {
    text: "var(--civic-cream)", muted: "rgba(var(--civic-cream-rgb),0.72)", line: "rgba(var(--civic-cream-rgb),0.14)",
    tile: "rgba(255,255,255,0.04)", good: "#6bc9a0", bad: "#ff7082", accent: "#7da6d4", chipOn: "var(--civic-gold)", chipOnInk: "#0a1628",
  } : {
    text: "var(--civic-ink)", muted: "rgba(var(--civic-ink-rgb),0.66)", line: "rgba(var(--civic-ink-rgb),0.12)",
    tile: "rgba(0,40,104,0.035)", good: "#0F6B49", bad: "#9e1b2b", accent: "var(--civic-navy)", chipOn: "var(--civic-navy)", chipOnInk: "#fff",
  };
  const median = band.median;
  const vsMedian = (price: number) => {
    if (!median) return null;
    const d = price - median, pct = Math.round((d / median) * 100);
    const sign = d < 0 ? "−" : "+";
    return { text: `${sign}${fmt(Math.abs(d))} (${sign}${Math.abs(pct)}%)`, color: d < 0 ? C.good : C.bad };
  };
  const verdictText = (v: DriveVerdict | null): { text: string; color: string } => {
    if (!v) return { text: "Nearest to you", color: C.accent };
    switch (v.kind) {
      case "worth_it": return { text: `Worth the drive · save ${fmt(v.saving)} (${Math.round(v.share * 100)}%)`, color: C.good };
      case "marginal": return { text: "Cheaper, not by enough to drive", color: C.muted };
      case "not_cheaper": return { text: "Not cheaper than the nearest", color: C.muted };
      case "no_baseline": return { text: "Nearest's price can't be compared", color: C.muted };
      default: return { text: "Price not usable for a comparison", color: C.muted };
    }
  };
  const hospLink = (l: Local) => (
    <Link to={`/hospital/${l.id}`} className="hover:underline" style={{ color: C.text, fontWeight: 600 }}>{titleCaseHospital(l.info.n)}</Link>
  );

  const bandEl = (
    <PriceRangeBand
      p10={band.p10 ?? undefined} median={band.median ?? undefined} avg={band.avg ?? undefined} p90={band.p90 ?? undefined}
      dark={dark} compact aa lesson={false} pinCaption={false}
      labels={{ p10: "CHEAPEST 10%", median: "MEDIAN", avg: "AVERAGE", p90: "PRICIEST 10%", pin: "NEAREST TO YOU" }}
      hospital={nearest ? { name: titleCaseHospital(nearest.info.n), price: nearest.price } : null}
      subject="for this scenario"
      localRange={localRange}
    />
  );

  // ---- controls ------------------------------------------------------------------
  const zipForm = (
    <form className="inline-flex items-center gap-1.5" onSubmit={(e) => { e.preventDefault(); if (/^\d{5}$/.test(zip)) { p.onZip(zip); setZipOpen(false); setRadiusTouched(false); } }}>
      <label className="sr-only" htmlFor="near-zip">ZIP code</label>
      <input id="near-zip" inputMode="numeric" maxLength={5} placeholder="ZIP" value={zip} onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
        className="h-8 w-20 rounded-md px-2 text-sm" style={{ border: `1px solid ${C.line}`, background: "transparent", color: C.text }} />
      <button type="submit" className="h-8 rounded-md px-2.5 text-xs font-semibold" style={{ border: `1px solid ${C.line}`, color: C.text }}>Go</button>
    </form>
  );
  const locateBtn = (label: string) => (
    <button type="button" onClick={() => { setRadiusTouched(false); p.onDetect(); }} disabled={p.isLocating}
      className="inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-xs font-semibold" style={{ border: `1px solid ${C.line}`, color: C.text }}>
      {p.isLocating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LocateFixed className="h-3.5 w-3.5" />}{label}
    </button>
  );

  if (!location) {
    return (
      <div className="mb-3">
        {bandEl}
        <div className="mt-3 rounded-lg p-3" style={{ border: `1px dashed ${C.line}`, background: C.tile }}>
          <p className="text-sm font-semibold" style={{ color: C.text }}>See what this costs at the hospitals near you</p>
          <p className="mt-0.5 text-xs" style={{ color: C.muted }}>The nearest five, the cheapest and the priciest within driving distance, and where each ranks nationally.</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">{locateBtn("Use my location")}{zipForm}</div>
          {p.error && <p className="mt-1.5 text-xs" style={{ color: C.bad }} role="alert">{p.error}</p>}
        </div>
      </div>
    );
  }

  const callout = (kind: string, l: Local | null, emptyText: string) => (
    <div className="min-w-0 rounded-lg p-2.5" style={{ border: `1px solid ${C.line}`, background: C.tile }}>
      <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: C.muted }}>{kind}</p>
      {l ? (
        <>
          <p className="mt-0.5 text-lg font-bold tabular-nums" style={{ color: C.text }}>{fmt(l.price)}</p>
          <p className="truncate text-xs">{hospLink(l)}</p>
          <p className="text-[11px]" style={{ color: C.muted }}>{titleCaseCity(l.info.c)}, {l.info.s} · {Math.round(l.miles)} mi</p>
          <p className="mt-1 text-[11px] font-semibold" style={{ color: l.cheaperThanPct >= 50 ? C.good : C.bad }}>{rankText(l)}</p>
        </>
      ) : <p className="mt-1 text-xs" style={{ color: C.muted }}>{emptyText}</p>}
    </div>
  );

  return (
    <div className="mb-3">
      {bandEl}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <p className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider" style={{ color: C.text }}>
          <MapPin className="h-3.5 w-3.5" style={{ color: C.accent }} />
          Near {location.label}{location.source === "ip" && <span className="font-normal normal-case tracking-normal" style={{ color: C.muted }}> (approximate)</span>}
          <button type="button" className="ml-1 font-semibold normal-case tracking-normal underline" style={{ color: C.accent }} onClick={() => setZipOpen((v) => !v)}>change</button>
        </p>
        <div className="flex items-center gap-1" role="group" aria-label="Distance">
          {NEAR_RADII.map((r) => (
            <button key={r} type="button" aria-pressed={radius === r} onClick={() => { setRadiusTouched(true); p.onRadius(r); }}
              className="h-7 rounded-full px-2.5 text-xs font-semibold"
              style={radius === r ? { background: C.chipOn, color: C.chipOnInk } : { border: `1px solid ${C.line}`, color: C.text }}>
              {r} mi
            </button>
          ))}
        </div>
      </div>
      {zipOpen && <div className="mt-2 flex flex-wrap items-center gap-2">{zipForm}{locateBtn("Use precise location")}</div>}

      {!pool ? (
        <p className="mt-3 inline-flex items-center gap-2 text-xs" style={{ color: C.muted }}><Loader2 className="h-3.5 w-3.5 animate-spin" /> Pricing nearby hospitals…</p>
      ) : local.length === 0 ? (
        <p className="mt-3 text-sm" style={{ color: C.muted }}>No hospital within {radius} miles publishes enough of this scenario to price it.</p>
      ) : (
        <>
          <p className="mt-2 text-sm" style={{ color: C.text }}>
            <b>{local.length}</b> {local.length === 1 ? "hospital" : "hospitals"} within {radius} mi {local.length === 1 ? "prices" : "price"} this
            {cheapest && priciest && cheapest.id !== priciest.id && <> · <b>{fmt(cheapest.price)}</b> to <b>{fmt(priciest.price)}</b></>}
            {!radiusTouched && local.length < MIN_NEARBY && <span style={{ color: C.muted }}> — fewer than {MIN_NEARBY} even at {radius} mi</span>}
          </p>
          <div className="mt-2 grid gap-2 sm:grid-cols-3">
            {callout("Nearest to you", nearest, "—")}
            {callout(`Cheapest within ${radius} mi`, cheapest, "No usable nearby price")}
            {callout(`Priciest within ${radius} mi`, priciest, "No usable nearby price")}
          </div>

          <div className="mt-3">
            <NearbyQualityTable
              rows={local} nearestId={nearest?.id ?? null} verdictOf={verdictOf} median={band.median} dark={dark} initial={10}
              caption={`Hospitals within ${radius} miles of ${location.label} that price this scenario: price, U.S. rank and quality`}
            />
          </div>
        </>
      )}
    </div>
  );
}
